import React, { useContext } from 'react'
import { useIntl } from 'react-intl'
import { AppContext } from '../../utils/appProvider'
import { parseLanguageAndRegionFromLocale } from '../../utils/helper'
import { MAP } from '../../utils/enums'
import useMessages from '../../hooks/useMessages'

import * as SC from './SalonMapEmbedStyles'
import GoogleMapsError from '../GoogleMapsError/GoogleMapsError'

export type SalonMapEmbedProps = {
	lat?: number
	lng?: number
	formattedAddress?: string | null
	salonName?: string | null
}

export const buildSalonMapEmbedSrc = (options: { apiKey: string; locale: string; lat?: number; lng?: number }): string | null => {
	const { apiKey, locale, lat, lng } = options

	const q = lat !== undefined && lng !== undefined ? `${lat},${lng}` : ''

	if (!q) {
		return null
	}

	const { language, region } = parseLanguageAndRegionFromLocale(locale)
	const params = new URLSearchParams({
		key: apiKey,
		q,
		zoom: String(MAP.placeZoom)
	})
	if (language) {
		params.set('language', language)
	}
	if (region) {
		params.set('region', region)
	}

	return `https://www.google.com/maps/embed/v1/place?${params.toString()}`
}

/**
 * Salon location map via [Maps Embed API](https://developers.google.com/maps/documentation/embed/get-started) (iframe).
 * When you need just a simple map to show the salon location, use this component.
 * If you need more advanced map features, use the Google Maps JavaScript API.
 * The advantage of using the Maps Embed API is that it’s free: map loads aren’t charged the way they are with dynamic maps from the Google Maps JavaScript API.
 * The downside is that it’s less customizable.
 * We were seeing costs go up on the project, so we chose this component instead.
 */

const SalonMapEmbed: React.FC<SalonMapEmbedProps> = ({ lat, lng, formattedAddress, salonName }) => {
	const { locale } = useIntl()
	const { googleMapsApiKey } = useContext(AppContext)
	const { messages } = useMessages()

	const src = buildSalonMapEmbedSrc({
		apiKey: googleMapsApiKey,
		locale,
		lat,
		lng
	})

	if (!src) {
		return <GoogleMapsError />
	}

	return (
		<SC.IframeContainer>
			<iframe
				title={salonName?.trim() || formattedAddress?.trim() || messages.Map}
				width='100%'
				height='100%'
				loading='lazy'
				referrerPolicy='no-referrer-when-downgrade'
				allowFullScreen
				src={src}
			/>
		</SC.IframeContainer>
	)
}

export default SalonMapEmbed
